/**
 * Campaign drafts, their two-figure audience, the proof, the send and the board (CSR-033).
 *
 * Every mutation invalidates the whole `notifications` tree: a send changes the list, the
 * audience and the board at once, and invalidating each by hand is how one gets missed.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type CampaignRow = components['schemas']['CampaignRow'];
export type CampaignIn = components['schemas']['CampaignIn'];
export type CampaignPatchIn = components['schemas']['CampaignPatchIn'];
export type CampaignAudience = components['schemas']['CampaignAudience'];
export type CampaignProgress = components['schemas']['CampaignProgress'];
export type CampaignTestSend = components['schemas']['CampaignTestSend'];
type CampaignList = components['schemas']['CampaignList'];

const BASE = '/v1/admin/notifications/campaigns';

export function useCampaigns(page = 1) {
  return useQuery({
    queryKey: keys.notifications.campaigns(page),
    queryFn: () => authedClient.request<CampaignList>(`${BASE}?page=${page}`),
  });
}

export function useCampaignAudience(id: number) {
  return useQuery({
    queryKey: keys.notifications.campaignAudience(id),
    queryFn: () => authedClient.request<CampaignAudience>(`${BASE}/${id}/audience`),
  });
}

export function useCampaignProgress(id: number, live: boolean) {
  return useQuery({
    queryKey: keys.notifications.campaignProgress(id),
    queryFn: () => authedClient.request<CampaignProgress>(`${BASE}/${id}/progress`),
    // Poll only while recipients are being handed on; a finished board does not move.
    refetchInterval: live ? 5000 : false,
  });
}

function useCampaignMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.notifications.all() });
    },
  });
}

export function useCreateCampaign() {
  return useCampaignMutation((body: CampaignIn) =>
    authedClient.request<CampaignRow>(BASE, { method: 'POST', body }),
  );
}

/** Edit a draft. Any field sent replaces it, and any edit voids the recorded test-send. */
export function usePatchCampaign() {
  return useCampaignMutation(({ id, body }: { id: number; body: CampaignPatchIn }) =>
    authedClient.request<CampaignRow>(`${BASE}/${id}`, { method: 'PATCH', body }),
  );
}

export function useTestSendCampaign() {
  return useCampaignMutation((id: number) =>
    authedClient.request<CampaignTestSend>(`${BASE}/${id}/test-send`, { method: 'POST' }),
  );
}

export function useSendCampaign() {
  return useCampaignMutation(({ id, confirm_count }: { id: number; confirm_count: number }) =>
    authedClient.request<CampaignRow>(`${BASE}/${id}/send`, {
      method: 'POST',
      body: { confirm_count },
    }),
  );
}

export type CampaignControl = 'pause' | 'resume' | 'cancel';

export function useControlCampaign() {
  return useCampaignMutation(({ id, control }: { id: number; control: CampaignControl }) =>
    authedClient.request<CampaignRow>(`${BASE}/${id}/${control}`, { method: 'POST' }),
  );
}
